import React, { useRef, useState } from 'react';
import { Input } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { ArrowUp, SlidersHorizontal } from 'lucide-react';
import { FilterPopover } from './FilterPopover';
import TypewriterPlaceholder from './TypewriterPlaceholder';
import useCheckIsMobile from '@/lib/hooks/useCheckIsMobile';

const { TextArea } = Input;

// 预设文案类型
export interface PresetPrompt {
  title: number;
  prompt: string;
}

interface Props {
  prompts?: PresetPrompt[];
  onSubmit?: (query: string, filters: { country: string; followerRange: [number, number] }) => void;
  isLogin?: boolean;
  onTriggerLogin?: () => void;
}

const loopColors = ['#f0f8ec', '#f1ebdc', '#f0f5ff', '#f1edfd', '#f3e7e4'];

export default function AIChatTextarea({ prompts = [], onSubmit, isLogin, onTriggerLogin }: Props) {
  const textareaRef = useRef<TextAreaRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [country, setCountry] = useState('');
  const [followerRange, setFollowerRange] = useState<[number, number]>([0, 7]);
  const { isMobile } = useCheckIsMobile();

  // 多句话循环显示 - 使用 prompts 列表里的 prompt 字段
  const placeholderTexts = prompts.length > 0 ? prompts.map((item) => item.prompt) : [];

  // 点击预设文案填充到输入框
  const handlePresetClick = (text: string) => {
    setQuery(text);
    // 聚焦到 textarea 并将光标移到最后
    setTimeout(() => {
      const textarea = textareaRef.current?.resizableTextArea?.textArea;
      if (textarea) {
        textarea.focus();
        // 将光标移到文本最后
        const length = text.length;
        textarea.setSelectionRange(length, length);
      }
    }, 0);
  };

  // 检查是否有激活的筛选条件
  const hasActiveFilters = country !== '' || followerRange[0] !== 0 || followerRange[1] !== 7;

  const handleSubmit = (e?: React.FormEvent | React.KeyboardEvent) => {
    e?.preventDefault();
    if (query.trim()) {
      // 未登录时弹出登录弹框
      if (!isLogin) {
        onTriggerLogin?.();
        return;
      }
      onSubmit?.(query, { country, followerRange });
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative rounded-3xl border-2 border-[#d9d9d9] bg-white shadow-sm hover:shadow-md focus-within:shadow-lg focus-within:border-[#725cff]/50 transition-[border-color,box-shadow] duration-200 ease-in-out">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            {/* TextArea 占据整个区域 */}
            <TextArea
              ref={textareaRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => {
                setIsFocused(true);
                // 移动端聚焦时滚动到距离顶部120px位置，防止虚拟键盘遮挡
                if (isMobile && containerRef.current) {
                  setTimeout(() => {
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (rect) {
                      const scrollTop = window.scrollY + rect.top - 120;
                      window.scrollTo({ top: scrollTop, behavior: 'smooth' });
                    }
                  }, 300);
                }
              }}
              onBlur={() => setIsFocused(false)}
              onKeyDown={(e) => {
                // 如果正在输入法组合输入（合成事件），不触发提交
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && query.trim()) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              autoSize={{ minRows: 3, maxRows: 6 }}
              className="w-full bg-transparent resize-none text-base ![&:focus]:shadow-none border-none rounded-[22px]"
              style={{
                padding: '20px 20px 52px 20px',
                minHeight: '120px',
                boxShadow: 'none',
              }}
              styles={{
                textarea: {
                  boxShadow: 'none !important',
                  borderRadius: '22px !important',
                },
              }}
            />

            {/* Placeholder 定位在 textarea 内，占据输入区域 */}
            {!query && !isFocused && (
              <div className="absolute top-[20px] left-[20px] right-[20px] bottom-[52px] pointer-events-none">
                <TypewriterPlaceholder texts={placeholderTexts} typingSpeed={60} deletingSpeed={30} pauseTime={2000} />
              </div>
            )}

            {/* 按钮组定位在 textarea 底部 */}
            <div className="absolute bottom-[12px] left-[20px] right-[20px] flex justify-between items-center pointer-events-none">
              <div className="pointer-events-auto">
                <FilterPopover
                  open={isFilterOpen}
                  onOpenChange={setIsFilterOpen}
                  country={country}
                  onCountryChange={setCountry}
                  followerRange={followerRange}
                  onFollowerRangeChange={setFollowerRange}
                  trigger={
                    <button
                      type="button"
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                        hasActiveFilters ? 'bg-[#725cff] text-white' : 'bg-[#ececf0] text-[#717182] hover:bg-[#e0e0e5]'
                      }`}
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                    </button>
                  }
                />
              </div>
              <div className="pointer-events-auto">
                <button
                  type="submit"
                  disabled={!query.trim()}
                  className="flex-shrink-0 w-8 h-8 rounded-full bg-[#725cff] text-white disabled:bg-[#ececf0] disabled:text-[#717182] flex items-center justify-center transition-all duration-200 hover:scale-105 disabled:hover:scale-100 disabled:opacity-50"
                >
                  <ArrowUp className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      {/* 预设文案提示 - 移动端支持左右滑动 */}
      <div className="mt-3 md:mt-6 flex gap-2 overflow-x-auto max-md:w-screen max-md:relative max-md:-left-4 max-md:px-4 scrollbar-hide pb-1 md:flex-wrap md:overflow-x-visible">
        {prompts.map((item, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handlePresetClick(item.prompt)}
            className="flex-shrink-0 px-4 py-2 rounded-full text-sm text-[#030213] transition-all duration-200 hover:opacity-80 active:scale-95"
            style={{ backgroundColor: loopColors[index % 5] }}
          >
            {item.title}
          </button>
        ))}
      </div>
    </div>
  );
}
